/**
 * 用户词汇详情 /users/$id/vocabulary（旧 views/user/UserVocabularyView.vue）：
 * 用户信息、学习概览、按熟悉度分值降序的词表、一键清除学习数据
 */
import { useEffect, useState } from 'react'
import { App, Button, Card, Col, Row, Statistic } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import type { VocabularyWord } from '@/api/services/admin'
import { useUserVocabulary } from './hooks'
import { VocabularyListTab } from './VocabularyListTab'
import { ClearUserDataModal } from './ClearUserDataModal'
import styles from './UserVocabularyPage.module.css'

const routeApi = getRouteApi('/admin/users/$id/vocabulary')
const DEFAULT_PAGE_SIZE = 50

export function UserVocabularyPage() {
  const { message } = App.useApp()
  const router = useRouter()
  const userId = Number(routeApi.useParams({ select: (params) => params.id }))
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE })
  const [selectedWord, setSelectedWord] = useState<VocabularyWord | null>(null)
  const [clearOpen, setClearOpen] = useState(false)
  const [shownUserId, setShownUserId] = useState(userId)
  const { data, isFetching, isError, error, isPending, refetch } = useUserVocabulary(
    userId,
    pagination.page,
    pagination.pageSize
  )

  // 切换到另一个用户时从第一页重新开始
  if (shownUserId !== userId) {
    setShownUserId(userId)
    setPagination({ page: 1, pageSize: DEFAULT_PAGE_SIZE })
    setSelectedWord(null)
  }

  useEffect(() => {
    if (!isError) return
    const detail = (error as any)?.response?.data?.detail || error?.message
    message.error('获取词汇数据失败：' + detail)
  }, [isError, error, message])

  // 清除数据后回到第一页，关闭证据抽屉（词表由 mutation 失效后重新请求）
  const handleCleared = () => {
    setSelectedWord(null)
    setPagination((current) => ({ ...current, page: 1 }))
  }

  const user = data?.user
  const summary = data?.summary

  return (
    <div>
      <div className={styles.back}>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => router.history.back()}>
          返回用户列表
        </Button>
      </div>

      <Card className={styles.card} loading={isPending}>
        {user && (
          <div className={styles.userHeader}>
            <h2 className={styles.userTitle}>
              用户详情：{user.display_name || user.username} (ID: {user.id})
            </h2>
            <Button type="primary" danger onClick={() => setClearOpen(true)}>
              🗑️ 一键清除数据
            </Button>
          </div>
        )}
      </Card>

      <Card title="📊 学习概览" className={styles.card} loading={isPending}>
        {summary && (
          <Row gutter={16}>
            <Col span={12}>
              <Statistic title="总遇见词汇" value={summary.total_words} suffix="个" />
            </Col>
            <Col span={12}>
              <Statistic title="熟悉度总分" value={summary.familiarity_score} />
            </Col>
          </Row>
        )}
      </Card>

      <Card title="词汇明细">
        <VocabularyListTab
          userId={userId}
          words={data?.vocabulary ?? []}
          total={data?.pagination.total ?? 0}
          loading={isFetching}
          page={pagination.page}
          pageSize={pagination.pageSize}
          selectedWord={selectedWord}
          onSelectWord={setSelectedWord}
          onPageChange={(page, pageSize) => setPagination({ page, pageSize })}
          onRefresh={() => refetch()}
        />
      </Card>

      <ClearUserDataModal
        open={clearOpen}
        userId={userId}
        onClose={() => setClearOpen(false)}
        onCleared={handleCleared}
      />
    </div>
  )
}
